// src/components/aols/profile/Stat.tsx
import React, { useState, useEffect } from "react";
import { Layout, Typography, Table, Button } from "antd";
import { Helmet } from "react-helmet-async";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { Footer } from "../../dev/utils/Footer";
import MiniOlsmeMonitor from "../../auth/components/MiniOlsmeMonitor";
import "./Stat.css";

const { Title } = Typography;
const { Header, Content } = Layout;

// Type Definitions
interface Dimensions {
  width: number;
}

interface Profile {
  key: string;
  link: string;
  image: string;
  name: string;
  olsDays: string;
  stats: string;
  lightDays: string;
  dailyAver: string;
  aOLSLM: string;
  olsm: string;
}

// Styled Components
const StyledLayout = styled(Layout)`
  min-height: 100vh;
  background: #000000;
`;

const StyledHeader = styled(Header)`
  display: flex;
  align-items: center;
  justify-content: center;
  background: #1a1a1a;
  padding: 15px 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
  transition: transform 0.3s ease;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 101;

  &:hover {
    transform: translateY(-2px);
  }
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.9;
  }

  &:focus {
    outline: 2px solid #ffd700;
    outline-offset: 2px;
  }
`;

const LogoImage = styled.img`
  height: 50px;
  margin-right: 20px;
  border-radius: 6px;
  object-fit: cover;
`;

const StyledContent = styled(Content)`
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 64px;
  background: #000000;
`;

const MenuBarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 900px;
  margin-bottom: 20px;
`;

const MenuBar = styled.div`
  background: #222;
  color: #ffffff;
  padding: 10px;
  text-align: center;
  font-size: 16px;
  font-weight: 500;
  border-radius: 6px;
  transition: background 0.3s ease;

  &:hover {
    background: #333;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  flex-wrap: wrap;
`;

const NavButton = styled(Button)`
  background: #ffcc00;
  border: none;
  color: #0427f4;
  font-size: 16px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 6px;
  transition: background 0.3s ease, transform 0.3s ease;

  &:hover {
    background: #ffaa00;
    color: #0427f4;
    transform: scale(1.05);
  }

  &:focus {
    outline: 2px solid #ffd700;
    outline-offset: 2px;
  }

  a {
    color: #0427f4;
    text-decoration: none;
  }

  a:hover {
    color: #0427f4;
  }
`;

const ProfileContainer = styled.div`
  background: #111;
  color: #ffffff;
  max-width: 900px;
  width: 100%;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.7);
  }
`;

const CallToActionButton = styled(Button)`
  margin-top: 40px;
  margin-bottom: 20px;
  background: linear-gradient(45deg, #ffcc00, #ffaa00, #ff8800);
  color: #ffffff;
  border: none;
  font-size: 18px;
  font-weight: 600;
  padding: 12px 30px;
  border-radius: 25px;
  box-shadow: 0 4px 15px rgba(255, 204, 0, 0.5);
  transition: background 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease;

  &:hover {
    background: linear-gradient(45deg, #ffaa00, #ff8800, #ff6600);
    box-shadow: 0 6px 20px rgba(255, 204, 0, 0.7);
    transform: translateY(-2px);
  }

  &:focus {
    outline: 2px solid #ffd700;
    outline-offset: 2px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const Stat: React.FC = () => {
  const [dimensions, setDimensions] = useState<Dimensions>({ width: window.innerWidth });

  useEffect(() => {
    const updateDimensions = () => {
      setDimensions({ width: window.innerWidth });
    };
    window.addEventListener("resize", updateDimensions);
    updateDimensions();
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  const profileData: Profile[] = [
    {
      key: "1",
      link: "/aa",
      image:
        "https://firebasestorage.googleapis.com/v0/b/olsdata-550ld.appspot.com/o/images%2F972.3499.olslm.png?alt=media&token=2fcf9749-a69e-483a-afca-465f5737911c",
      name: "Asvitloaten",
      olsDays: "2945",
      stats: "19931",
      lightDays: "13.84",
      dailyAver: "6.76",
      aOLSLM: "a1.8",
      olsm: "1952",
    },
    {
      key: "2",
      link: "/rwh",
      image:
        "https://images.crunchbase.com/image/upload/c_thumb,h_170,w_170,f_auto,g_face,z_0.7,b_white,q_auto:eco,dpr_2/mzatdfshnkstd0hnebmj",
      name: "ReiWenHo",
      olsDays: "2809",
      stats: "7170",
      lightDays: "4.97",
      dailyAver: "2.46",
      aOLSLM: "a0.5",
      olsm: "352",
    },
    {
      key: "3",
      link: "/ah",
      image:
        "https://media.licdn.com/dms/image/v2/D5603AQFO2oVdSlBbHw/profile-displayphoto-shrink_100_100/profile-displayphoto-shrink_100_100/0/1692414820173?e=1747872000&v=beta&t=1gayEM0eHWzgNb1_N-6-OqBlvLnSOPx8gjT1-cJ2Kek",
      name: "A.H.",
      olsDays: "531",
      stats: "343",
      lightDays: "0.23",
      dailyAver: "1.45",
      aOLSLM: "",
      olsm: "3",
    },
  ];

  const columns = [
    {
      title: "OLSdays",
      dataIndex: "olsDays",
      key: "olsDays",
      width: 60,
      render: (text: string) => <span style={{ color: "#ffffff", fontSize: 14 }}>{text}</span>,
    },
    {
      title: "Profile",
      dataIndex: "profile",
      key: "profile",
      width: 140,
      render: (_: unknown, record: Profile) => (
        <a
          href={record.link}
          style={{
            textDecoration: "none",
            color: "#ffffff",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
          aria-label={`View profile for ${record.name}`}
        >
          <img
            src={record.image}
            alt={`${record.name} profile`}
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).src = "https://via.placeholder.com/40";
            }}
          />
          <span style={{ fontSize: 14, fontWeight: 500 }}>{record.name}</span>
        </a>
      ),
    },
    {
      title: () => (
        <span
          style={{
            background: "#cc9900",
            color: "#ffffff",
            padding: "4px 8px",
            borderRadius: "6px",
            fontSize: "14px",
            fontWeight: 600,
          }}
        >
          OLSLM
        </span>
      ),
      dataIndex: "stats",
      key: "stats",
      width: 120,
      render: (text: string) => <span style={{ color: "#ffffff", fontSize: 14 }}>{text}</span>,
    },
    {
      title: "Light Days",
      dataIndex: "lightDays",
      key: "lightDays",
      width: 100,
      render: (text: string) => <span style={{ color: "#ffffff", fontSize: 14 }}>{text}</span>,
    },
    {
      title: "Daily Aver.",
      dataIndex: "dailyAver",
      key: "dailyAver",
      width: 100,
      render: (text: string) => (
        <span style={{ color: "#ffffff", fontSize: 14 }}>{text || "N/A"}</span>
      ),
    },
    {
      title: () => (
        <Link to="/aOLSLMasset" style={{ textDecoration: "none" }}>
          <span
            style={{
              background: "#2a44d6",
              color: "#ffffff",
              padding: "4px 8px",
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: 600,
            }}
          >
            aOLSLM
          </span>
        </Link>
      ),
      dataIndex: "aOLSLM",
      key: "aOLSLM",
      width: 120,
      render: (text: string) => (
        <span style={{ color: "#ffffff", fontSize: 14 }}>{text || "N/A"}</span>
      ),
    },
    {
      title: () => (
        <Link to="/olsme" style={{ textDecoration: "none" }}>
          <span
            style={{
              background: "#683fa0",
              color: "#ffffff",
              padding: "4px 8px",
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: 600,
            }}
          >
            OLSM
          </span>
        </Link>
      ),
      dataIndex: "olsm",
      key: "olsm",
      width: 100,
      render: (text: string) => (
        <span style={{ color: "#ffffff", fontSize: 14 }}>{text || "N/A"}</span>
      ),
    },
  ];

  const tableData = profileData.map((item) => ({
    key: item.key,
    link: item.link,
    image: item.image,
    name: item.name,
    olsDays: item.olsDays,
    stats: item.stats,
    lightDays: item.lightDays,
    dailyAver: item.dailyAver,
    aOLSLM: item.aOLSLM,
    olsm: item.olsm,
  }));

  return (
    <StyledLayout>
      <Helmet>
        <title>OLS Student Statistics | OneLightSystem - Meditation Insights</title>
        <meta
          name="description"
          content="Explore detailed student statistics for OneLightSystem OLS in the Sun Light Meditation Academy. View OLSLM (Light Minutes), Light Days, daily averages, and available assets for meditation practitioners."
        />
        <meta
          name="keywords"
          content="OLS student statistics, OLSLM, Light Minutes, Light Days, daily average meditation, Sun Light Meditation, OLS Meditation Education Academy, Light Civilization Development, OneLightSystem OLS, meditation stats"
        />
        <meta name="author" content="OneLightSystem OLS" />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta property="og:title" content="OLS Student Statistics | OneLightSystem - Meditation Insights" />
        <meta
          property="og:description"
          content="Discover detailed student statistics for OneLightSystem OLS, including OLSLM (Light Minutes), Light Days, daily averages, and available assets in the Sun Light Meditation Academy."
        />
        <meta property="og:url" content="https://www.olsme.com/aols/profile/stat" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.olsme.com/logo.jpg" />
        <meta property="og:image:alt" content="OneLightSystem OLS Logo" />
        <meta property="og:site_name" content="OneLightSystem OLS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="OLS Student Statistics | OneLightSystem - Meditation Insights" />
        <meta
          name="twitter:description"
          content="Explore OneLightSystem OLS student stats, including OLSLM (Light Minutes), Light Days, daily averages, and available assets in the Sun Light Meditation Academy."
        />
        <meta name="twitter:image" content="https://www.olsme.com/logo.jpg" />
        <meta name="twitter:image:alt" content="OneLightSystem OLS Logo" />
        <meta name="twitter:site" content="@onelightsystem" />
        <meta name="twitter:creator" content="@onelightsystem" />
        <link rel="canonical" href="https://www.olsme.com/aols/profile/stat" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "OLS Student Statistics",
            description:
              "Explore detailed student statistics for OneLightSystem OLS in the Sun Light Meditation Academy, including OLSLM (Light Minutes), Light Days, daily averages, and available assets.",
            url: "https://www.olsme.com/aols/profile/stat",
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://www.olsme.com/",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "AOLS",
                  item: "https://www.olsme.com/aols",
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: "Profile",
                  item: "https://www.olsme.com/aols/profile",
                },
                {
                  "@type": "ListItem",
                  position: 4,
                  name: "Statistics",
                  item: "https://www.olsme.com/aols/profile/stat",
                },
              ],
            },
            publisher: {
              "@type": "EducationalOrganization",
              name: "OneLightSystem OLS",
              description:
                "OneLightSystem OLS provides student statistics for the Sun Light Meditation Academy, including OLSLM (Light Minutes), Light Days, daily averages, and available assets.",
              url: "https://www.olsme.com",
              logo: "https://www.olsme.com/logo.jpg",
              contactPoint: {
                "@type": "ContactPoint",
                email: "info@olsme.com",
                contactType: "Customer Service",
              },
              address: {
                "@type": "PostalAddress",
                addressLocality: "Belmont",
                addressRegion: "CA",
                addressCountry: "US",
              },
              founder: {
                "@type": "Person",
                name: "Nazar Pankiv",
                jobTitle: "CEO",
              },
              sameAs: [
                "https://www.olsme.com",
                "https://www.ols-med.net",
                "https://twitter.com/onelightsystem",
                "https://www.facebook.com/onelightsystemols",
                "https://www.linkedin.com/company/one-light-system",
                "https://www.youtube.com/@ONELIGHTSYSTEMOLSMeditation",
              ],
            },
            mainEntity: {
              "@type": "Offer",
              name: "OLS Student Program",
              description:
                "Join the OneLightSystem OLS Sun Light Meditation Academy to track your meditation progress, including OLSLM, Light Days, and more.",
              url: "https://www.olsme.com/register/olssregister",
            },
          })}
        </script>
      </Helmet>

      <StyledHeader>
        <LogoLink to="/login" aria-label="Return to login page">
          <LogoImage src="/logo.jpg" alt="OLS Logo" aria-label="OLS Logo" />
        </LogoLink>
        <Title level={2} style={{ color: "#ffffff", margin: 0, fontWeight: 600 }}>
          onelightsystemOLS Meditation Education
        </Title>
      </StyledHeader>

      <Layout>
        <StyledContent>
          <MenuBarContainer>
            <MenuBar>OLS teacher OLS inuo OLS student</MenuBar>
            <ButtonRow>
              <NavButton>
                <Link to="/lightScience" aria-label="Navigate to Light Science page">
                  onelightsystem
                </Link>
              </NavButton>
              <NavButton>
                <Link to="/OLSaeo" aria-label="Navigate to OLS iee.aeo page">
                  OLS iee.aeo
                </Link>
              </NavButton>
              <NavButton>
                <Link to="/OLSstudentProspect" aria-label="Navigate to OLS Student Prospect page">
                  OLS student prospect
                </Link>
              </NavButton>
            </ButtonRow>
          </MenuBarContainer>

          <ProfileContainer>
            <Table
              className="styled-table"
              columns={columns}
              dataSource={tableData}
              pagination={false}
              size="middle"
              scroll={{ x: dimensions.width < 768 ? 600 : undefined }}
            />
          </ProfileContainer>

          <ButtonContainer>
            <CallToActionButton
              href="/register/olssregister"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Apply to become an OLS student"
            >
              Apply to Become an OLS Student
            </CallToActionButton>
            <MiniOlsmeMonitor />
          </ButtonContainer>
        </StyledContent>
      </Layout>

      <Footer />
    </StyledLayout>
  );
};

export default Stat;